import { ArrowRight } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import {
  Divider,
  Grid,
  InputLabel,
  MenuItem,
  type MenuProps,
  Select,
  type SelectProps,
  Stack,
  type StackProps,
  type SxProps,
  Tooltip,
  type TooltipProps,
  Typography,
  useTheme,
} from "@mui/material";
import isObjectEmpty from "../../../helpers/isObjectEmpty";
import useIsMobile from "../../../hooks/useIsMobile";
import { type MenuItemData, NestedMenuItem } from "mui-nested-menu";
import {
  type CSSProperties,
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";

export interface DropDownProps {
  name: string;
  keyValue: string;
  keyLabel?: string;
  data: any[];
  fullWidth?: boolean;
  sx?: SxProps;
  disabled?: boolean;
  placeholder?: string;
  setValue?: (value: string, prev: string, index?: any) => any;
  /**
   * Used in conditions like form-within-form, where the field value setter is external.
   */
  setFieldValue?: (field: string, value: any, shouldValidate?: any) => void;
  index?: number;
  plain?: boolean;
  value?: string;
  error?: boolean;
  multilevel?: boolean;
  menuItemsData?: MenuItemData;
  menuItemSx?: SxProps;
  MenuProps?: Partial<MenuProps>;
  NestedMenuProps?: Partial<MenuProps>;
  className?: string;
  SelectProps?: SelectProps;
  label?: string;
  required?: boolean;
  helpText?: TooltipProps["title"];
  direction?: StackProps["direction"];
  dense?: boolean;
  optionsListMinHeight?: CSSProperties["minHeight"];
  [x: string]: any;
}

const Drop = ({
  name,
  data = [],
  value,
  keyValue,
  keyLabel,
  fullWidth,
  sx,
  index,
  placeholder = "Select",
  setValue = () => {},
  setFieldValue,
  plain,
  error,
  multilevel = false,
  menuItemSx,
  MenuProps,
  NestedMenuProps,
  disabled,
  className,
  SelectProps,
  required,
  label,
  helpText,
  optionsListMinHeight = 100,
  direction = "column",
  dense = false,
  ...rest
}: DropDownProps) => {
  // Memoize formikValue to prevent unnecessary re-calculations
  const formikValue = useMemo(() => {
    return !isObjectEmpty(rest) && rest?.hasOwnProperty("values")
      ? rest["values"][name]
      : value;
  }, [rest, name, value]);

  const isMobile = useIsMobile();
  const [dropdownValue, setCurrentValue] = useState("");
  const [open, setOpen] = useState(false);
  const [itemSelected, setitemSelected] = useState(false);
  const [menuItemsData, setMenuItemsData] = useState<any>();

  // Use ref to track if we're updating from internal change (to prevent loops)
  const isInternalUpdate = useRef(false);

  // Sync dropdown value with formik/prop value - with protection against infinite loops
  useEffect(() => {
    // Skip if this is an internal update
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const newValue =
      formikValue !== undefined && formikValue !== null ? formikValue : "";
    // Only update if the value actually changed to prevent infinite loops
    if (newValue !== dropdownValue) {
      setCurrentValue(newValue);
    }
  }, [formikValue, dropdownValue]);
  const handleChange = (event: any) => {
    setitemSelected(true);
    setOpen(false);
    let newValue;
    if (isMobile) {
      newValue = event.target.value || "";
    } else {
      // For non-native, get value from event.target.value or event.target.getAttribute('data-value')
      newValue =
        event.target.value || event.target.getAttribute("data-value") || "";
    }

    // Prevent unnecessary updates if value hasn't changed
    if (dropdownValue === newValue) {
      return;
    }

    // Mark this as an internal update to prevent useEffect from triggering
    isInternalUpdate.current = true;

    // Update all value sources
    setCurrentValue(newValue);
    setValue(newValue, dropdownValue, index);
    setFieldValue ? setFieldValue(name, newValue) : null;
  };

  const theme = useTheme();
  useEffect(() => {
    if (itemSelected) setOpen(false);
  }, [itemSelected]);

  useEffect(() => {
    if (multilevel && data && data.length > 0) {
      const menuData = {
        label:
          dropdownValue === "" ? placeholder : (dropdownValue ?? placeholder),
        items: data.map((item: any, i: number) => {
          const items = item.items?.map((subItem: any, i: number) => {
            return {
              uid: subItem?.uid ?? `${subItem[keyValue]}-${i}`,
              label: subItem?.label ?? subItem[keyValue],
              display: "none",
              callback: () => {
                setitemSelected(true);
                setOpen(false);

                const newValue = subItem[keyValue];
                // Mark as internal update
                isInternalUpdate.current = true;
                setCurrentValue(newValue);
                setValue(newValue, dropdownValue, index);
                setFieldValue ? setFieldValue(name, newValue, true) : null;
              },
              ...subItem,
            };
          });

          return {
            uid: item?.uid ?? `${item[keyValue]}-${i}`,
            label: item?.label ?? item[keyValue],
            display: "block",
            callback: () => {
              setitemSelected(true);
              setOpen(false);
              const newValue = item[keyValue];
              // Mark as internal update
              isInternalUpdate.current = true;
              setCurrentValue(newValue);
              setValue(newValue, dropdownValue, index);
              setFieldValue ? setFieldValue(name, newValue, true) : null;
            },
            ...item,
            sx: menuItemSx,
            items,
          };
        }),
      };

      setMenuItemsData({
        ...menuData,
        items: menuData.items
          .map((item) => {
            const toReturn = [item];
            if (item.items?.length) toReturn.push(item.items);
            return toReturn.flat();
          })
          .flat(),
      });
    }
  }, [multilevel, data.length, placeholder, keyValue, index, name]);

  const minHeight = data?.length
    ? 38 * data.length > 250
      ? 250
      : 53 * data.length
    : optionsListMinHeight;
  const Label = ({ label }: { label: string }) => (
    <Stack
      alignItems="center"
      justifyContent="start"
      spacing={0.5}
      direction="row"
      mb={dense ? 0.25 : 0.5}
      height={dense ? "auto" : "2.3em"}
      width="100%"
    >
      <Typography
        fontWeight={dense ? 600 : "bold"}
        fontSize={dense ? "0.75rem" : undefined}
        color={dense ? "text.secondary" : undefined}
      >
        {label && `${label}`}
      </Typography>
      {required && <Typography color="error">*</Typography>}
      {Boolean(helpText) && (
        <Tooltip title={helpText} enterTouchDelay={0} leaveDelay={200}>
          <InfoIcon color="primary" sx={{ marginTop: dense ? 0 : "6px" }} />
        </Tooltip>
      )}
      {!dense && <InputLabel />}
    </Stack>
  );
  const commonSx = {
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      height: "auto",
      "> div": {
        height: "auto",
        "> input": {
          height: "0.8em",
          fontSize: "0.8em",
        },
      },
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "0.8em",
      marginTop: label ? "0" : "10px",
      height: "2.9em",
      ">div": {
        height: "2.3em",
        ">input": {
          height: "0.8em",
          fontSize: "0.8em",
        },
      },
    },

    "&.Mui-disabled": {
      ".MuiOutlinedInput-notchedOutline": {
        borderRadius: 0.5,
      },
      ">div": {
        color: theme.palette.text.disabled,
        textFillColor: theme.palette.text.disabled,
        WebkitTextFillColor: theme.palette.text.disabled,
        backgroundColor: "rgb(191 191 191 / 50%)",
        borderRadius: 0.5,
      },
      ">select": {
        backgroundColor: "rgba(80, 79, 108, 0.5)",
      },
      backgroundColor: "rgba(80, 79, 108, 0.5)",
      borderRadius: 0.5,
      backgroundClip: "content-box",
    },
  };
  if (multilevel) {
    return (
      <Stack
        direction={direction}
        alignItems={direction === "row" ? "center" : "start"}
        justifyContent="start"
      >
        {label && <Label label={label} />}
        <Tooltip
          title={
            <Typography fontWeight="bold">{`${dropdownValue || placeholder}`}</Typography>
          }
        >
          <Select
            disabled={disabled}
            open={open}
            error={error}
            data-testid={`multiselect-dropdown-${name}`}
            placeholder={placeholder}
            value={dropdownValue}
            displayEmpty
            variant={plain ? "standard" : "outlined"}
            fullWidth={fullWidth}
            MenuProps={{
              ...MenuProps,
              PaperProps: {
                sx: {
                  maxHeight: minHeight,
                  ...MenuProps?.PaperProps?.sx,
                },
              },
            }}
            className={className}
            sx={
              plain
                ? {
                    borderBottom: "none !important",
                    "&:before": {
                      borderBottom: "none",
                    },
                    "&:hover:not(.Mui-disabled):before": {
                      borderBottom: "none",
                    },
                    height: "2.2em",
                    ...commonSx,
                    ...sx,
                  }
                : {
                    marginTop: dense ? 0 : "8px",
                    verticalAlign: "top",
                    marginBottom: dense ? 0 : "4px",
                    textTransform: "capitalize",
                    height: dense ? "40px" : "2.2em",
                    ">div": {
                      borderRadius: 1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      maxWidth: "76%",
                      textOverflow: "ellipsis",
                    },
                    ...commonSx,
                    ...(dense ? { height: 40, marginTop: 0 } : {}),
                    ...sx,
                  }
            }
            name={name}
            onChange={handleChange}
            onClick={() => {
              setOpen(!open);
            }}
            {...SelectProps}
            aria-label={`${label || placeholder || name} dropdown`}
            title={`${label || placeholder || name} dropdown`}
            /* eslint-disable-next-line jsx-a11y/control-has-associated-label, jsx-a11y/no-noninteractive-element-interactions */
          >
            <MenuItem value="" disabled>
              {placeholder}
            </MenuItem>

            {menuItemsData?.items &&
              menuItemsData?.items.map((item: any, i: number) => {
                if (item.items) {
                  return (
                    <Grid
                      container
                      spacing={1}
                      key={item.uid || `${item.label}-${i}`}
                      paddingLeft="0.5em"
                    >
                      <Grid item xs={12}>
                        <NestedMenuItem
                          rightIcon={<ArrowRight />}
                          label={item.label}
                          parentMenuOpen={open}
                          nonce={undefined}
                          MenuProps={NestedMenuProps}
                          data-testid={`nested-dropdown-${name}`}
                        >
                          {item.items &&
                            item.items.map((subItem: any, subIndex: number) => {
                              return (
                                <MenuItem
                                  key={
                                    subItem.uid ||
                                    `${subItem.label}-${subIndex}`
                                  }
                                  value={subItem.label}
                                  onClick={subItem.callback}
                                  data-testid={`nested-menuitem-${subItem[keyValue]}`}
                                >
                                  <Grid container spacing={1}>
                                    <Grid
                                      item
                                      sx={{
                                        wordWrap: "break-word",
                                        whiteSpace: "normal",
                                        maxWidth: "30vw",
                                      }}
                                    >
                                      {subItem[keyValue]}
                                    </Grid>
                                    {item.items.length > 1 && (
                                      <Grid item xs={12}>
                                        <Divider />
                                      </Grid>
                                    )}
                                  </Grid>
                                </MenuItem>
                              );
                            })}
                        </NestedMenuItem>
                      </Grid>
                      <Grid item xs={11}>
                        <Divider />
                      </Grid>
                    </Grid>
                  );
                }
                if (
                  dropdownValue &&
                  item.items?.find((val: any) => val.label === dropdownValue)
                ) {
                  const currentItem = item.items?.find(
                    (val: any) => val.label === dropdownValue,
                  );

                  return (
                    <MenuItem
                      key={
                        currentItem?.uid ||
                        `${currentItem[keyValue]}-${dropdownValue}`
                      }
                      value={dropdownValue}
                      onClick={currentItem.callback}
                    >
                      {dropdownValue}
                    </MenuItem>
                  );
                }

                return (
                  <MenuItem
                    key={item.uid || `${item.label}-${i}`}
                    value={item.label}
                    sx={{
                      display: item.display,
                    }}
                    onClick={item.callback}
                    data-testid={`dropdown-${name}`}
                  >
                    <Grid container spacing={1}>
                      <Grid
                        item
                        xs={12}
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          maxWidth: "76%",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item[keyValue]}
                      </Grid>
                      {open && (
                        <Grid item xs={12}>
                          <Divider />
                        </Grid>
                      )}
                    </Grid>
                  </MenuItem>
                );
              })}
          </Select>
        </Tooltip>
      </Stack>
    );
  }
  return (
    <Stack
      direction={direction}
      alignItems="center"
      justifyContent="start"
      sx={{ width: "100%" }}
    >
      {label && <Label label={label} />}
      <Select
        error={error}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={`dropdown-${name}`}
        value={dropdownValue}
        native={isMobile}
        MenuProps={{
          ...MenuProps,
          // Rendering in the document body keeps the menu anchored to its Select,
          // rather than to a dashboard container with its own layout/overflow rules.
          keepMounted: MenuProps?.keepMounted ?? true,
          disablePortal: MenuProps?.disablePortal ?? false,
          PaperProps: {
            ...MenuProps?.PaperProps,
            sx: {
              minHeight,
              maxWidth: "min(100vw - 32px, 480px)",
              "& .MuiMenuItem-root": {
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              },
              ...MenuProps?.PaperProps?.sx,
            },
          },
        }}
        displayEmpty
        renderValue={(selected) => {
          if (!selected || selected === "") {
            return <em>{placeholder}</em>;
          }
          if (keyLabel) {
            const selectedItem = data.find(
              (item) => item[keyValue] === selected,
            );
            return selectedItem ? selectedItem[keyLabel] : selected;
          }
          return selected;
        }}
        variant={plain ? "standard" : "outlined"}
        fullWidth={fullWidth}
        sx={
          plain
            ? {
                borderBottom: "none !important",
                "&:before": {
                  borderBottom: "none",
                },
                "&:hover:not(.Mui-disabled):before": {
                  borderBottom: "none",
                },
                height: "2.2em",
                ...commonSx,
                ...sx,
              }
            : {
                marginTop: dense ? 0 : "8px",
                verticalAlign: "top",
                marginBottom: dense ? 0 : "4px",
                ...commonSx,
                ...(dense ? { height: 40, marginTop: 0 } : {}),
                ...sx,
              }
        }
        name={name}
        onChange={handleChange}
        {...SelectProps}
        aria-label={`${label || placeholder || name} dropdown`}
        title={`${label || placeholder || name} dropdown`}
      >
        {isMobile ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : (
          <MenuItem value="" disabled>
            {placeholder}
          </MenuItem>
        )}

        {data &&
          data.map((item: any) => {
            const displayText = keyLabel ? item[keyLabel] : item[keyValue];
            return isMobile ? (
              <option
                key={item.uid || `menuitem-${item[keyValue]}`}
                value={item[keyValue]}
                disabled={item.disabled}
                data-testid={`menuitem-${item[keyValue]}`}
              >
                {displayText}
              </option>
            ) : (
              <MenuItem
                key={item.uid || `menuitem-${item[keyValue]}`}
                value={item[keyValue]}
                disabled={item.disabled}
                data-testid={`menuitem-${item[keyValue]}`}
              >
                {displayText}
              </MenuItem>
            );
          })}
      </Select>
    </Stack>
  );
};
export default Drop;
